import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView, AnimatePresence } from "framer-motion";
import {
    FaFlask,
    FaChartLine,
    FaHeart,
    FaBell,
    FaRobot,
    FaMicroscope,
    FaBrain,
    FaExclamationTriangle,
    FaRunning,
    FaFire,
    FaDumbbell,
    FaTrophy,
    FaBolt,
    FaStopwatch,
    FaStethoscope,
    FaHeartbeat,
    FaChild,
    FaMapMarkerAlt,
    FaShieldAlt,
    FaMobile,
    FaWallet,
    FaPaperPlane,
    FaMicrophone,
    FaVolumeUp,
    FaCopy,
    FaCheck,
    FaThumbsUp,
    FaThumbsDown,
    FaRedo,
    FaMagic,
    FaLightbulb,
    FaCheckCircle,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import fa from "./locales/ai-feature-fa.json";
import en from "./locales/ai-feature-en.json";
import ar from "./locales/ai-feature-ar.json";
import tr from "./locales/ai-feature-tr.json";

// ─── Progress Bar ─────────────────────────────────────────────────────────────
const AnimatedProgressBar: React.FC<{ value: number; color: string; delay?: number }> = ({ value, color, delay = 0 }) => (
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: "0%" }}
            animate={{ width: `${value}%` }}
            transition={{ duration: 1.5, ease: "easeInOut", delay }}
        />
    </div>
);

// ─── Typing Dots ─────────────────────────────────────────────────────────────
const TypingDots: React.FC<{ color: string }> = ({ color }) => (
    <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
            <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: color }}
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            />
        ))}
    </div>
);

// ─── Switcher Button Multi-Layer Icons ──────────────────────────────────────────
const HealthMultiLayerIcon: React.FC<{ active?: boolean; className?: string }> = ({ active, className = "w-[74px] h-[74px]" }) => (
    <div className={`relative flex items-center justify-center ${className}`}>
        {/* Layer 1 (Outer Squircle Ring) */}
        <div
            className="absolute inset-0 rounded-[20px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 100%)"
                    : "linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(21,93,252,0.08) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(37,99,235,0.3)",
                boxShadow: active ? "inset 0 0 10px rgba(255,255,255,0.2)" : "0 4px 14px rgba(21,93,252,0.12)",
            }}
        />

        {/* Layer 2 (Middle Ring 1) */}
        <div
            className="absolute inset-[5px] rounded-[16px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 100%)"
                    : "linear-gradient(135deg, rgba(96,165,250,0.35) 0%, rgba(37,99,235,0.15) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.5)" : "1px solid rgba(96,165,250,0.4)",
            }}
        />

        {/* Layer 3 (Middle Ring 2) */}
        <div
            className="absolute inset-[10px] rounded-[13px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.35) 100%)"
                    : "linear-gradient(135deg, rgba(147,197,253,0.5) 0%, rgba(96,165,250,0.25) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.7)" : "1px solid rgba(147,197,253,0.6)",
            }}
        />

        {/* Layer 4 (Inner Core Glow) */}
        <div
            className="absolute inset-[15px] rounded-[10px] flex items-center justify-center transition-all duration-300"
            style={{
                background: active
                    ? "radial-gradient(circle, rgba(255,255,255,0.98) 0%, rgba(219,234,254,0.85) 60%, rgba(147,197,253,0.7) 100%)"
                    : "radial-gradient(circle, rgba(239,246,255,1) 0%, rgba(191,219,254,0.9) 60%, rgba(59,130,246,0.5) 100%)",
                boxShadow: active
                    ? "0 2px 10px rgba(255,255,255,0.7), 0 0 14px rgba(37,99,235,0.8)"
                    : "0 2px 8px rgba(21,93,252,0.3)",
            }}
        >
            {/* Center Blue Hexagon Face Icon */}
            <svg className="w-6 h-6 relative z-10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 2.5L19.7942 7V17L12 21.5L4.20577 17V7L12 2.5Z"
                    fill={active ? "#155DFC" : "#1D4ED8"}
                />
                <circle cx="9.2" cy="10" r="1.1" fill="#FFFFFF" />
                <circle cx="14.8" cy="10" r="1.1" fill="#FFFFFF" />
                <path d="M9.5 14C10.2 15.2 11 15.5 12 15.5C13 15.5 13.8 15.2 14.5 14" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        </div>
    </div>
);

const SportMultiLayerIcon: React.FC<{ active?: boolean; className?: string }> = ({ active, className = "w-[74px] h-[74px]" }) => (
    <div className={`relative flex items-center justify-center ${className}`}>
        {/* Layer 1 (Outer Squircle Ring) */}
        <div
            className="absolute inset-0 rounded-[20px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)"
                    : "linear-gradient(135deg, rgba(74,222,128,0.2) 0%, rgba(34,197,94,0.08) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(74,222,128,0.3)",
                boxShadow: active ? "inset 0 0 10px rgba(255,255,255,0.2)" : "0 4px 14px rgba(34,197,94,0.12)",
            }}
        />

        {/* Layer 2 (Middle Ring 1) */}
        <div
            className="absolute inset-[5px] rounded-[16px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.18) 100%)"
                    : "linear-gradient(135deg, rgba(134,239,172,0.35) 0%, rgba(74,222,128,0.15) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.5)" : "1px solid rgba(134,239,172,0.4)",
            }}
        />

        {/* Layer 3 (Middle Ring 2) */}
        <div
            className="absolute inset-[10px] rounded-[13px] transition-all duration-300"
            style={{
                background: active
                    ? "linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.3) 100%)"
                    : "linear-gradient(135deg, rgba(187,247,208,0.5) 0%, rgba(134,239,172,0.25) 100%)",
                border: active ? "1px solid rgba(255,255,255,0.7)" : "1px solid rgba(187,247,208,0.6)",
            }}
        />

        {/* Layer 4 (Inner Core Glow) */}
        <div
            className="absolute inset-[15px] rounded-[10px] flex items-center justify-center transition-all duration-300"
            style={{
                background: active
                    ? "radial-gradient(circle, rgba(255,255,255,0.98) 0%, rgba(220,252,231,0.85) 60%, rgba(134,239,172,0.7) 100%)"
                    : "radial-gradient(circle, rgba(240,253,244,1) 0%, rgba(187,247,208,0.9) 60%, rgba(74,222,128,0.5) 100%)",
                boxShadow: active
                    ? "0 2px 10px rgba(255,255,255,0.7), 0 0 14px rgba(74,222,128,0.8)"
                    : "0 2px 8px rgba(34,197,94,0.3)",
            }}
        >
            {/* Dumbbell Icon in Center Core */}
            <svg className="w-7 h-7 relative z-10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.36362 11.6279C5.75362 11.0189 4.76562 11.0089 4.14262 11.6069C3.73862 11.9959 3.33862 12.3909 2.95262 12.7989C2.33762 13.4519 2.35162 14.3169 2.97662 14.9629C3.19103 15.1851 3.57067 15.5632 3.8091 15.7996C3.92729 15.9168 3.92787 16.1077 3.81025 16.2255L3.17662 16.8599C2.63062 17.4049 2.63062 18.2899 3.17662 18.8359L5.16362 20.8229C5.70762 21.3679 6.59362 21.3679 7.13962 20.8229L7.77285 20.1881C7.89022 20.0705 8.08064 20.0704 8.19792 20.1882C8.42179 20.4129 8.7702 20.762 8.97762 20.9669C9.66962 21.6459 10.4966 21.6829 11.2066 21.0379C11.6266 20.6569 12.0296 20.2549 12.4116 19.8349C12.9856 19.2009 12.9596 18.2249 12.3576 17.6199C10.3626 15.6189 8.36462 13.6219 6.36362 11.6279Z"
                    fill={active ? "#15803D" : "#16A34A"}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.9654 8.97798C20.7612 8.77058 20.4124 8.42263 20.1877 8.19906C20.07 8.08187 20.0699 7.89152 20.1873 7.77405L20.8214 7.13998C21.3674 6.59398 21.3674 5.70898 20.8214 5.16398L18.8354 3.17698C18.2904 2.63098 17.4044 2.63098 16.8594 3.17698L16.2259 3.8105C16.1082 3.92818 15.9173 3.92772 15.8 3.80962C15.5633 3.57119 15.1847 3.19144 14.9624 2.97698C14.3164 2.35198 13.4504 2.33798 12.7984 2.95398C12.3904 3.33898 11.9954 3.73798 11.6064 4.14298C11.0094 4.76598 11.0174 5.75298 11.6264 6.36398C13.6214 8.36498 15.6184 10.363 17.6194 12.357C18.2254 12.961 19.2004 12.986 19.8334 12.412C20.2544 12.031 20.6584 11.626 21.0384 11.207C21.6844 10.497 21.6464 9.66998 20.9654 8.97798Z"
                    fill={active ? "#15803D" : "#16A34A"}
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.6849 9.13809C12.5369 8.99309 12.2949 8.99909 12.1489 9.14509C10.7629 10.5311 10.4919 10.8031 9.11787 12.1771C8.96887 12.3261 8.96687 12.5721 9.11787 12.7191L11.3198 14.8681C11.4678 15.0131 11.7049 15.0121 11.8519 14.8651C13.0869 13.6301 14.3558 12.3621 14.8948 11.8231C15.0438 11.6741 15.0388 11.4341 14.8878 11.2871L12.6849 9.13809Z"
                    fill={active ? "#15803D" : "#16A34A"}
                />
            </svg>
        </div>
    </div>
);

interface SwitcherBtnProps {
    active: boolean;
    color: string;
    label: string;
    imgSrc?: string;
    imgAlt?: string;
    icon?: React.ReactNode | ((active: boolean) => React.ReactNode);
    onClick: () => void;
    isMobile?: boolean;
}
const SwitcherBtn: React.FC<SwitcherBtnProps> = ({ active, color, label, imgSrc, imgAlt, icon, onClick, isMobile }) => {
    const [hovered, setHovered] = useState(false);

    // Dynamic gradient and shadow based on active state and color
    const isGreen = color.toLowerCase().includes("16a34a") || color.toLowerCase().includes("emerald") || color.toLowerCase().includes("green");
    const activeGradient = isGreen
        ? "linear-gradient(135deg, #15803D 0%, #16A34A 55%, #22C55E 100%)"
        : "linear-gradient(135deg, #1D4ED8 0%, #155DFC 55%, #3B82F6 100%)";
    const activeShadow = isGreen
        ? "0 12px 28px -6px rgba(22, 163, 74, 0.42), inset 0 1px 1.5px rgba(255, 255, 255, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.35)"
        : "0 12px 28px -6px rgba(21, 93, 252, 0.42), inset 0 1px 1.5px rgba(255, 255, 255, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.35)";

    if (isMobile) {
        return (
            <motion.button
                onClick={onClick}
                type="button"
                className={`relative overflow-hidden flex items-center justify-center py-2.5 px-4 flex-1 rounded-xl transition-all duration-300 cursor-pointer font-bold text-xs sm:text-sm`}
                style={{
                    background: active ? activeGradient : hovered ? "rgba(255,255,255,0.7)" : "transparent",
                    boxShadow: active ? `0 6px 18px -3px ${color}40` : "none",
                    color: active ? "#ffffff" : "#475569",
                }}
                whileHover={{ scale: active ? 1 : 1.02 }}
                whileTap={{ scale: 0.97 }}
            >
                {/* Shimmer effect on active mobile button */}
                {active && (
                    <motion.div
                        className="absolute inset-0 pointer-events-none rounded-xl"
                        style={{
                            background: "linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.25) 50%, transparent 75%)",
                            backgroundSize: "200% 100%",
                        }}
                        animate={{ backgroundPosition: ["200% 0", "-200% 0"] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    />
                )}
                <span className="relative z-10 tracking-tight">{label}</span>
            </motion.button>
        );
    }

    return (
        <motion.button
            onClick={onClick}
            type="button"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`group relative overflow-hidden flex flex-col items-center justify-center gap-2 p-2.5 w-[108px] h-[128px] rounded-[20px] transition-all duration-300 cursor-pointer`}
            style={{
                background: active ? activeGradient : hovered ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.45)",
                boxShadow: active ? activeShadow : hovered ? "0 8px 20px rgba(0,0,0,0.06)" : "0 2px 8px rgba(0,0,0,0.02)",
                border: active ? "1px solid rgba(255,255,255,0.4)" : hovered ? "1px solid rgba(203,213,225,0.85)" : "1px solid rgba(226,232,240,0.6)",
            }}
            whileHover={{ scale: active ? 1.02 : 1.04, y: -2 }}
            whileTap={{ scale: 0.96 }}
        >
            {/* Shimmer sweep on active */}
            {active && (
                <motion.div
                    className="absolute inset-0 pointer-events-none rounded-[20px]"
                    style={{
                        background: "linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.22) 50%, transparent 75%)",
                        backgroundSize: "250% 100%",
                    }}
                    animate={{ backgroundPosition: ["200% 0", "-200% 0"] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                />
            )}

            {/* Glowing top rim highlight */}
            <div
                className="absolute top-0 inset-x-3 h-[1px] rounded-full transition-opacity duration-300"
                style={{
                    background: active
                        ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)"
                        : hovered
                        ? `linear-gradient(90deg, transparent, ${color}60, transparent)`
                        : "transparent",
                }}
            />

            {/* Desktop Icon - Always rendered in both Active and Inactive states */}
            {icon ? (
                <div className="w-[74px] h-[74px] flex items-center justify-center relative z-10 transition-transform duration-300 group-hover:scale-105">
                    {typeof icon === "function" ? icon(active) : icon}
                </div>
            ) : imgSrc ? (
                <img
                    src={imgSrc}
                    alt={imgAlt}
                    className="w-[74px] h-[74px] object-contain relative z-10 transition-transform duration-300 group-hover:scale-105"
                    style={{
                        filter: active ? "drop-shadow(0 4px 12px rgba(255,255,255,0.4))" : `drop-shadow(0 4px 12px ${color}40)`,
                    }}
                />
            ) : null}

            {/* Label */}
            <span
                className={`font-bold text-[13px] tracking-tight relative z-10 transition-colors duration-200 ${
                    active ? "text-white drop-shadow-xs" : "text-slate-700 group-hover:text-slate-900"
                }`}
            >
                {label}
            </span>

            {/* Active Indicator Dot at the bottom */}
            {active && (
                <motion.div
                    layoutId="activeTabDot"
                    className="w-1.5 h-1.5 rounded-full bg-white/90 shadow-sm"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 350, damping: 25 }}
                />
            )}
        </motion.button>
    );
};

// ─── Small Feature Card ───────────────────────────────────────────────────────
interface SmallCardProps {
    feature: { icon: React.ReactNode; title: string; subtitle: string; color: string; hoverIcon: React.ReactNode; hoverText: string };
    delay?: number;
}
const SmallCard: React.FC<SmallCardProps> = ({ feature, delay = 0 }) => {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div
            ref={cardRef}
            className="group relative rounded-2xl sm:rounded-[20px] p-4 sm:p-5 md:p-6 overflow-hidden cursor-pointer bg-white/75 backdrop-blur-md transition-all duration-300"
            style={{
                border: isHovered ? `1px solid ${feature.color}40` : "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: isHovered
                    ? `0 20px 35px -10px ${feature.color}22, 0 4px 12px rgba(0,0,0,0.03)`
                    : "0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)",
            }}
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, delay, type: "spring", stiffness: 220, damping: 22 }}
            viewport={{ once: true }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Dynamic cursor-following spotlight */}
            <div
                className="pointer-events-none absolute -inset-px transition-opacity duration-300 rounded-2xl sm:rounded-[20px]"
                style={{
                    opacity: isHovered ? 1 : 0,
                    background: `radial-gradient(400px circle at ${mousePos.x}px ${mousePos.y}px, ${feature.color}14, transparent 65%)`,
                }}
            />

            {/* Subtle top accent line on hover */}
            <div
                className="absolute top-0 inset-x-0 h-[2px] transition-all duration-500 origin-center scale-x-0 group-hover:scale-x-100"
                style={{
                    background: `linear-gradient(90deg, transparent, ${feature.color}, transparent)`,
                }}
            />

            {/* Clean Icon Container with smooth kinetic feedback */}
            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
                <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 shadow-sm"
                    style={{
                        background: isHovered ? `${feature.color}15` : "rgba(241, 245, 249, 0.85)",
                        color: isHovered ? feature.color : "#64748b",
                        border: isHovered ? `1px solid ${feature.color}30` : "1px solid rgba(226, 232, 240, 0.6)",
                    }}
                >
                    <span className="text-lg sm:text-xl transition-transform duration-300 group-hover:scale-110">{feature.icon}</span>
                </div>

                <div
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0"
                    style={{
                        background: `${feature.color}12`,
                        color: feature.color,
                    }}
                >
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>

            <h3
                className="text-sm sm:text-base font-bold mb-1.5 sm:mb-2 transition-colors duration-300 text-slate-900 group-hover:text-slate-950"
                style={{ color: isHovered ? feature.color : undefined }}
            >
                {feature.title}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed transition-colors duration-300 group-hover:text-slate-700">
                {feature.subtitle}
            </p>
        </motion.div>
    );
};

// ─── Health Widget ────────────────────────────────────────────────────────────
const HealthWidget: React.FC<{ t: any }> = ({ t }) => {
    const [mode, setMode] = useState<"live" | "rest" | "active">("live");
    const [isScanning, setIsScanning] = useState(false);

    const modeData = {
        live: {
            hr: 74,
            spo2: 98,
            hrStatus: "ریتم سینوسی پایدار",
            spo2Status: "اشباع خون مطلوب",
            hrv: "۶۵ ms",
            aiTip: "شاخص‌های حیاتی در وضعیت تعادل و ضربان بهینه قرار دارند.",
            accent: "#2563eb",
        },
        rest: {
            hr: 58,
            spo2: 99,
            hrStatus: "ریکاوری عمیق",
            spo2Status: "اشباع خون ایده‌آل",
            hrv: "۸۲ ms",
            aiTip: "سیستم پاراسمپاتیک فعال است و بدن در فاز بازسازی سلولی قرار دارد.",
            accent: "#4f46e5",
        },
        active: {
            hr: 134,
            spo2: 96,
            hrStatus: "زون هوازی (Cardio)",
            spo2Status: "تهویه تنفسی پرتوان",
            hrv: "۴۲ ms",
            aiTip: "ضربان مناسب برای بهبود استقامت قلبی و حداکثر اکسیداسیون چربی.",
            accent: "#0284c7",
        },
    };

    const current = modeData[mode];

    const handleRescan = () => {
        setIsScanning(true);
        setTimeout(() => setIsScanning(false), 1200);
    };

    return (
        <motion.div className="mt-3 sm:mt-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
            <div
                className="p-3.5 sm:p-4 rounded-2xl border relative overflow-hidden"
                style={{
                    background: "linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(219,234,254,0.75) 100%)",
                    border: "1px solid rgba(37,99,235,0.22)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow: "0 4px 20px rgba(37,99,235,0.06)",
                }}
            >
                {/* Scanning overlay beam */}
                {isScanning && (
                    <motion.div
                        className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent pointer-events-none z-20"
                        initial={{ top: "0%" }}
                        animate={{ top: "100%" }}
                        transition={{ duration: 1.2, ease: "easeInOut" }}
                    />
                )}

                {/* Header & Interactive Mode Switcher */}
                <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2">
                        <motion.button
                            type="button"
                            onClick={handleRescan}
                            title="اسکن آنی بیومتریک"
                            aria-label="اسکن آنی بیومتریک"
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md shadow-blue-500/20 text-white cursor-pointer"
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.92 }}
                        >
                            <FaStethoscope className={`text-xs ${isScanning ? "animate-spin" : ""}`} />
                        </motion.button>
                        <div>
                            <div className="text-[10px] text-blue-600 font-bold leading-tight">پایش هوشمند بیومتریک</div>
                            <div className="text-xs font-extrabold text-blue-950">داشبورد وضعیت فیزیولوژیک</div>
                        </div>
                    </div>

                    {/* Interactive Filter Pills */}
                    <div className="flex items-center gap-1 bg-white/70 p-0.5 rounded-xl border border-blue-200/60 shadow-2xs">
                        {(
                            [
                                { id: "live", label: "زنده" },
                                { id: "rest", label: "استراحت" },
                                { id: "active", label: "فعالیت" },
                            ] as const
                        ).map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setMode(m.id)}
                                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all ${
                                    mode === m.id
                                        ? "bg-blue-600 text-white shadow-xs"
                                        : "text-blue-900/70 hover:text-blue-900 hover:bg-white/80"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Metric Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-2.5">
                    {/* Heart Rate Card */}
                    <motion.div
                        className="bg-white/90 rounded-xl p-2.5 border border-blue-100 shadow-2xs cursor-pointer transition-all"
                        whileHover={{ y: -1, borderColor: "rgba(37,99,235,0.4)" }}
                        onClick={() => setMode(mode === "live" ? "active" : mode === "active" ? "rest" : "live")}
                    >
                        <div className="flex justify-between items-center text-xs mb-1">
                            <span className="font-semibold text-gray-700 flex items-center gap-1 text-[11px]">
                                <FaHeartbeat className="text-blue-500 text-[10px] animate-pulse" />
                                {t("widgets.health.heartRate.label", { ns: "ai-feature" })}
                            </span>
                            <div className="flex items-baseline gap-0.5">
                                <motion.span
                                    key={current.hr}
                                    initial={{ scale: 1.2, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="font-extrabold text-blue-700 text-xs sm:text-sm"
                                >
                                    {current.hr}
                                </motion.span>
                                <span className="text-[9px] text-gray-400">{t("widgets.health.heartRate.unit", { ns: "ai-feature" })}</span>
                            </div>
                        </div>

                        {/* Interactive Mini Waveform */}
                        <div className="h-4 my-1 flex items-center justify-between gap-0.5 px-0.5 bg-blue-50/50 rounded">
                            {[30, 60, 45, 90, 25, 75, 40, 85, 30, 60, 100, 35].map((h, i) => (
                                <motion.span
                                    key={i}
                                    className="w-1 bg-blue-500/70 rounded-full"
                                    animate={{ height: [`${Math.max(15, (h * current.hr) / 100)}%`, `${Math.max(25, ((110 - h) * current.hr) / 100)}%`, `${Math.max(15, (h * current.hr) / 100)}%`] }}
                                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.08 }}
                                />
                            ))}
                        </div>

                        <div className="flex justify-between items-center mt-1">
                            <span className="text-[9px] text-gray-400">HRV: {current.hrv}</span>
                            <span className="text-[9px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                                {current.hrStatus}
                            </span>
                        </div>
                    </motion.div>

                    {/* Blood Oxygen Card */}
                    <motion.div
                        className="bg-white/90 rounded-xl p-2.5 border border-blue-100 shadow-2xs cursor-pointer transition-all"
                        whileHover={{ y: -1, borderColor: "rgba(37,99,235,0.4)" }}
                        onClick={() => setMode(mode === "live" ? "rest" : "live")}
                    >
                        <div className="flex justify-between items-center text-xs mb-1">
                            <span className="font-semibold text-gray-700 flex items-center gap-1 text-[11px]">
                                <FaHeart className="text-blue-500 text-[10px]" />
                                {t("widgets.health.bloodOxygen.label", { ns: "ai-feature" })}
                            </span>
                            <div className="flex items-baseline gap-0.5">
                                <motion.span
                                    key={current.spo2}
                                    initial={{ scale: 1.2, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="font-extrabold text-blue-700 text-xs sm:text-sm"
                                >
                                    {current.spo2}
                                </motion.span>
                                <span className="text-[9px] text-gray-400">{t("widgets.health.bloodOxygen.unit", { ns: "ai-feature" })}</span>
                            </div>
                        </div>

                        <div className="my-1.5">
                            <AnimatedProgressBar value={current.spo2} color="#2563eb" delay={0.1} />
                        </div>

                        <div className="flex justify-between items-center mt-1">
                            <span className="text-[9px] text-gray-400">سنسور اپتیکال</span>
                            <span className="text-[9px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                                {current.spo2Status}
                            </span>
                        </div>
                    </motion.div>
                </div>

                {/* Dynamic AI Biometric Insight */}
                <motion.div
                    key={mode}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-2 p-2 rounded-xl bg-blue-100/70 border border-blue-200/60 flex items-start gap-1.5"
                >
                    <FaRobot className="text-blue-600 text-[11px] mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] font-medium text-blue-950 leading-relaxed">
                        <span className="font-bold text-blue-800 ml-1">تحلیل آنی:</span>
                        {current.aiTip}
                    </p>
                </motion.div>
            </div>
        </motion.div>
    );
};

// ─── Sport Widget ────────────────────────────────────────────────────────────
const SportWidget: React.FC<{ t: any }> = ({ t }) => {
    const [activity, setActivity] = useState<"walk" | "run" | "hiit">("run");

    const activityData = {
        walk: {
            readiness: 94,
            readinessStatus: "آمادگی حداکثری",
            calories: 320,
            target: "۴,۰۰۰ قدم",
            vo2: "نرمال",
            tip: "ریکاوری فعال سبک؛ مناسب برای کاهش استرس و بهبود جریان خون عضلانی.",
        },
        run: {
            readiness: 88,
            readinessStatus: "آماده تمرین هوازی",
            calories: 650,
            target: "۸.۵ کیلومتر",
            vo2: "VO2 Max: ۴۸",
            tip: "ضربان پایدار در زون ۲؛ افزایش چشمگیر بازده قلبی و چربی‌سوزی پایدار.",
        },
        hiit: {
            readiness: 79,
            readinessStatus: "فشار تمرینی بالا",
            calories: 920,
            target: "۴۵ دقیقه تمرین",
            vo2: "VO2 Max: ۵۲",
            tip: "فاز پرفشار بی‌هوازی؛ ۱۵ دقیقه ریکاوری و الکترولیت پس از تمرین توصیه می‌شود.",
        },
    };

    const current = activityData[activity];

    return (
        <motion.div className="mt-3 sm:mt-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
            <div
                className="p-3.5 sm:p-4 rounded-2xl border"
                style={{
                    background: "linear-gradient(135deg, rgba(240,253,244,0.95) 0%, rgba(220,252,231,0.75) 100%)",
                    border: "1px solid rgba(22,163,74,0.22)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow: "0 4px 20px rgba(22,163,74,0.06)",
                }}
            >
                {/* Header & Interactive Activity Switcher */}
                <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center shadow-md shadow-emerald-500/20 text-white"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <FaRunning className="text-xs" />
                        </motion.div>
                        <div>
                            <div className="text-[10px] text-emerald-600 font-bold leading-tight">مربی هوشمند ورزشی</div>
                            <div className="text-xs font-extrabold text-emerald-950">شاخص آمادگی و عملکرد</div>
                        </div>
                    </div>

                    {/* Activity Selector Pills */}
                    <div className="flex items-center gap-1 bg-white/70 p-0.5 rounded-xl border border-emerald-200/60 shadow-2xs">
                        {(
                            [
                                { id: "walk", label: "پیاده‌روی" },
                                { id: "run", label: "هوازی" },
                                { id: "hiit", label: "اینتروال" },
                            ] as const
                        ).map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => setActivity(a.id)}
                                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all ${
                                    activity === a.id
                                        ? "bg-emerald-600 text-white shadow-xs"
                                        : "text-emerald-900/70 hover:text-emerald-900 hover:bg-white/80"
                                }`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Metric Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-2.5">
                    {/* Readiness Card */}
                    <motion.div
                        className="bg-white/90 rounded-xl p-2.5 border border-emerald-100/80 shadow-2xs cursor-pointer transition-all"
                        whileHover={{ y: -1, borderColor: "rgba(22,163,74,0.4)" }}
                        onClick={() => setActivity(activity === "run" ? "hiit" : activity === "hiit" ? "walk" : "run")}
                    >
                        <div className="flex justify-between items-center text-xs mb-1">
                            <span className="font-semibold text-gray-700 flex items-center gap-1 text-[11px]">
                                <FaBolt className="text-emerald-600 text-[10px]" />
                                {t("widgets.sport.readiness.label", { ns: "ai-feature" })}
                            </span>
                            <motion.span
                                key={current.readiness}
                                initial={{ scale: 1.2, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="font-extrabold text-emerald-700 text-xs sm:text-sm"
                            >
                                {current.readiness}%
                            </motion.span>
                        </div>
                        <div className="my-1.5">
                            <AnimatedProgressBar value={current.readiness} color="#16a34a" />
                        </div>
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-[9px] text-gray-400">شاخص ریکاوری</span>
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                                {current.readinessStatus}
                            </span>
                        </div>
                    </motion.div>

                    {/* Calories & Goal Card */}
                    <motion.div
                        className="bg-white/90 rounded-xl p-2.5 border border-emerald-100/80 shadow-2xs cursor-pointer transition-all"
                        whileHover={{ y: -1, borderColor: "rgba(22,163,74,0.4)" }}
                        onClick={() => setActivity(activity === "run" ? "walk" : "run")}
                    >
                        <div className="flex justify-between items-center text-xs mb-1">
                            <span className="font-semibold text-gray-700 flex items-center gap-1 text-[11px]">
                                <FaFire className="text-orange-500 text-[10px]" />
                                {t("widgets.sport.calories.label", { ns: "ai-feature" })}
                            </span>
                            <div className="flex items-baseline gap-0.5">
                                <motion.span
                                    key={current.calories}
                                    initial={{ scale: 1.2, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="font-extrabold text-emerald-700 text-xs sm:text-sm"
                                >
                                    {current.calories}
                                </motion.span>
                                <span className="text-[9px] text-gray-400">{t("widgets.sport.calories.unit", { ns: "ai-feature" })}</span>
                            </div>
                        </div>
                        <div className="my-1.5">
                            <AnimatedProgressBar value={Math.min(100, Math.round((current.calories / 1000) * 100))} color="#22c55e" delay={0.1} />
                        </div>
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-[9px] text-gray-400">{current.target}</span>
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                                {current.vo2}
                            </span>
                        </div>
                    </motion.div>
                </div>

                {/* Dynamic AI Coach Recommendation */}
                <motion.div
                    key={activity}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-2 p-2 rounded-xl bg-emerald-100/70 border border-emerald-200/60 flex items-start gap-1.5"
                >
                    <FaRobot className="text-emerald-700 text-[11px] mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] font-medium text-emerald-950 leading-relaxed">
                        <span className="font-bold text-emerald-800 ml-1">توصیه مربی:</span>
                        {current.tip}
                    </p>
                </motion.div>
            </div>
        </motion.div>
    );
};

/*
// ─── Child Widget (Commented out as requested) ────────────────────────────────
const ChildWidget: React.FC<{ t: any }> = ({ t }) => (
    <motion.div className="mt-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
        <div
            className="p-4 rounded-2xl"
            style={{
                background: "linear-gradient(135deg, rgba(239,246,255,0.9) 0%, rgba(219,234,254,0.7) 100%)",
                border: "1px solid rgba(59,130,246,0.2)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "0 4px 20px rgba(59,130,246,0.08)",
            }}
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <motion.div
                        className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-md"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <FaChild className="text-white text-sm" />
                    </motion.div>
                    <div>
                        <div className="text-[10px] text-blue-600 font-medium">{t("widgets.child.header.subtitle", { ns: "ai-feature" })}</div>
                        <div className="text-xs font-bold text-blue-900">{t("widgets.child.header.title", { ns: "ai-feature" })}</div>
                    </div>
                </div>
                <motion.span
                    className="flex items-center gap-1 text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                    {t("common.active", { ns: "ai-feature" })}
                </motion.span>
            </div>

            <div className="space-y-3">
                <motion.div className="bg-white/70 rounded-xl p-3 border border-blue-100" whileHover={{ y: -2, boxShadow: "0 6px 16px rgba(59,130,246,0.12)", borderColor: "rgba(59,130,246,0.3)" }}>
                    <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-700 text-xs">{t("widgets.child.location.label", { ns: "ai-feature" })}</span>
                        <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1 h-1 bg-green-500 rounded-full" />
                            {t("widgets.child.location.statusSafe", { ns: "ai-feature" })}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <motion.div animate={{ y: [0, -2, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                            <FaMapMarkerAlt className="text-blue-500" />
                        </motion.div>
                        <span>{t("widgets.child.location.place", { ns: "ai-feature" })}</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">{t("widgets.child.location.lastUpdate", { ns: "ai-feature" })}</div>
                </motion.div>

                <motion.div className="bg-white/70 rounded-xl p-3 border border-blue-100" whileHover={{ y: -2, boxShadow: "0 6px 16px rgba(59,130,246,0.12)", borderColor: "rgba(59,130,246,0.3)" }}>
                    <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-700 text-xs">{t("widgets.child.todayActivity.label", { ns: "ai-feature" })}</span>
                        <span className="text-xs font-bold text-gray-900">۸۵٪</span>
                    </div>
                    <AnimatedProgressBar value={85} color="#3b82f6" delay={0.3} />
                    <div className="text-[10px] text-gray-400 mt-1">{t("widgets.child.todayActivity.details", { ns: "ai-feature" })}</div>
                </motion.div>
            </div>

            <motion.div
                className="mt-3 p-3 rounded-xl"
                style={{ background: "rgba(219,234,254,0.5)", border: "1px solid rgba(59,130,246,0.15)" }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.5, delay: 1.2 }}
            >
                <div className="flex items-center gap-2 mb-1.5">
                    <motion.div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                        <FaRobot className="text-blue-500 text-[9px]" />
                    </motion.div>
                    <span className="text-xs font-bold text-blue-700">{t("widgets.child.aiReport.title", { ns: "ai-feature" })}</span>
                </div>
                <p className="text-[11px] text-blue-700 leading-relaxed">{t("widgets.child.aiReport.text", { ns: "ai-feature" })}</p>
            </motion.div>
        </div>
    </motion.div>
);
*/

// ─── Chat Message Interface ──────────────────────────────────────────────────
interface ChatMessage {
    id: string;
    sender: "user" | "bot";
    text: string;
    time: string;
    metrics?: Array<{ label: string; value: string; status?: "good" | "warning" | "optimal" }>;
    tip?: string;
    reaction?: "up" | "down" | null;
}

// ─── Chat Widget ──────────────────────────────────────────────────────────────
const ChatWidget: React.FC<{
    type: "health" | "sport";
    featureTitle: string;
    t: any;
}> = ({ type, featureTitle, t }) => {
    const isHealth = type === "health";
    const color = isHealth ? "#155DFC" : "#16A34A";
    const Icon = isHealth ? FaHeartbeat : FaRunning;
    const gradFrom = isHealth ? "from-blue-600" : "from-emerald-600";
    const gradTo = isHealth ? "to-blue-700" : "to-green-700";
    const bgFrom = isHealth ? "from-blue-50/95" : "from-emerald-50/95";
    const bgTo = isHealth ? "to-blue-100/70" : "to-green-100/70";
    const borderColor = isHealth ? "border-blue-200/70" : "border-emerald-200/70";
    const titleColor = isHealth ? "text-blue-700" : "text-emerald-800";
    const pillBg = isHealth
        ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300/50"
        : "bg-gradient-to-r from-emerald-100 to-green-200 text-emerald-800 border-emerald-300/50";
    const assistantTitle = isHealth
        ? t("chat.healthAssistantTitle", { ns: "ai-feature" })
        : t("chat.sportAssistantTitle", { ns: "ai-feature" });
    const sendBtnFrom = isHealth ? "from-blue-600 to-blue-700" : "from-emerald-600 to-green-700";

    // Quick Prompts per Mode
    const quickPrompts = isHealth
        ? [
              { id: "q1", label: "💓 تحلیل نوار قلب و آریتمی", icon: FaHeartbeat },
              { id: "q2", label: "🫁 سطح اکسیژن در خواب", icon: FaBrain },
              { id: "q3", label: "🩺 علت خستگی و ریتم استراحت", icon: FaStethoscope },
              { id: "q4", label: "💧 میزان مصرف بهینه آب", icon: FaBolt },
          ]
        : [
              { id: "s1", label: "⚡ بهترین زمان تمرین امروز چیه؟", icon: FaBolt },
              { id: "s2", label: "🔥 محاسبه کالری‌سوزی و زون ۳", icon: FaFire },
              { id: "s3", label: "🏃 برنامه افزایش استقامت و VO2", icon: FaRunning },
              { id: "s4", label: "🏋️ زمان ریکاوری عضلات", icon: FaDumbbell },
          ];

    // Initial Messages
    const initialMessages: ChatMessage[] = isHealth
        ? [
              {
                  id: "msg-1",
                  sender: "bot",
                  text: `سلام! من دستیار هوشمند سلامت شما هستم. کلیه شاخص‌های بیومتریک شما از جمله ضربان استراحت (۷۲ bpm) و سطح اکسیژن (۹۸٪) پایش شده و وضعیت فیزیولوژیک شما در حالت پایدار و نرمال است.`,
                  time: "همین الان",
                  metrics: [
                      { label: "ریتم سینوسی", value: "نرمال و یکنواخت", status: "good" },
                      { label: "شاخص استرس", value: "۱۸٪ (پایین)", status: "optimal" },
                  ],
                  tip: "پیشنهاد: نوشیدن یک لیوان آب قبل از خواب به بهبود چرخه REM کمک می‌کند.",
              },
          ]
        : [
              {
                  id: "msg-1",
                  sender: "bot",
                  text: `درود ورزشکار! شاخص آمادگی تمرینی (Training Readiness) شما ۸۸٪ و در وضعیت اوج است. عضلات شما پس از تمرین دیروز به خوبی ریکاوری شده‌اند و امروز بهترین زمان برای تمرینات استقامتی یا هوازی تناوبی است.`,
                  time: "همین الان",
                  metrics: [
                      { label: "آمادگی بدنی", value: "۸۸٪ (اوج)", status: "optimal" },
                      { label: "هدف کالری", value: "۶۵۰ / ۸۰۰ kcal", status: "good" },
                  ],
                  tip: "پیشنهاد مربی: ۴۰ دقیقه دویدن در زون ۳ (ضربان ۱۴۰-۱۵۵) برای تقویت حداکثر توان قلبی.",
              },
          ];

    const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [isSpeakingId, setIsSpeakingId] = useState<string | null>(null);
    const [isListening, setIsListening] = useState(false);
    const chatContainerRef = useRef<HTMLDivElement>(null);

    // Reset when assistant type changes
    useEffect(() => {
        setMessages(initialMessages);
        setInputValue("");
        setIsTyping(false);
        setIsSpeakingId(null);
    }, [type]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    const handleSend = (textToSend?: string) => {
        const text = (textToSend || inputValue).trim();
        if (!text || isTyping) return;

        const userMsg: ChatMessage = {
            id: `usr-${Date.now()}`,
            sender: "user",
            text,
            time: "همین الان",
        };

        setMessages((prev) => [...prev, userMsg]);
        setInputValue("");
        setIsTyping(true);

        setTimeout(() => {
            let botText = "";
            let metrics: ChatMessage["metrics"] = undefined;
            let tip = "";

            if (isHealth) {
                if (text.includes("قلب") || text.includes("نوار") || text.includes("آریتمی")) {
                    botText = "تحلیل نوار قلب ۳ کاناله شما هیچ‌گونه آریتمی یا انحراف قطعه ST را نشان نمی‌دهد. ضربان قلب شما کاملاً متقارن بوده و میانگین در حالت نشسته ۷۲ ضربان در دقیقه است.";
                    metrics = [
                        { label: "فاصله PR", value: "۰.۱۶ ثانیه (طبیعی)", status: "good" },
                        { label: "شاخص HRV", value: "۶۴ میلی‌ثانیه", status: "optimal" },
                    ];
                    tip = "برای حفظ تعادل HRV، روزانه تمرین تنفس ۴-۷-۸ انجام دهید.";
                } else if (text.includes("اکسیژن") || text.includes("خواب") || text.includes("تنفس")) {
                    botText = "میانگین اشباع اکسیژن خون شما (SpO2) در طول چرخه خواب عمیق ۹۸.۲٪ بوده است و هیچ افتی زیر ۹۵٪ ثبت نشده است. مسیر هوایی شما در تمام مدت خواب کاملاً باز بوده است.";
                    metrics = [
                        { label: "میانگین SpO2", value: "۹۸.۲٪", status: "optimal" },
                        { label: "توقف تنفسی (AHI)", value: "۰.۴ در ساعت (عالی)", status: "good" },
                    ];
                    tip = "دمای اتاق را در حدود ۱۹ درجه سانتی‌گراد نگه دارید تا کیفیت تنفس به حداکثر برسد.";
                } else {
                    botText = `پاسخ هوشمند به: "${text}". داده‌های بیومتریک و زیستی شما نشان‌دهنده هماهنگی عالی سیستم قلبی-عروقی و متابولیک است. پارامترهای پایه‌ای شما در محدوده سبز قرار دارند.`;
                    metrics = [
                        { label: "ثبات فیزیولوژیک", value: "۹۶٪", status: "optimal" },
                        { label: "شاخص انرژی", value: "نرمال", status: "good" },
                    ];
                    tip = "پایش مستمر سنسورها فعال است؛ در صورت تغییر ناگهانی، اعلان دریافت خواهید کرد.";
                }
            } else {
                if (text.includes("زمان") || text.includes("شروع") || text.includes("برنامه")) {
                    botText = "بر اساس نوسانات دمای مرکزی بدن و سطح کورتیزول، بهترین بازه زمانی برای تمرین با شدت بالای شما امروز بین ساعت ۱۷:۰۰ تا ۱۹:۳۰ است؛ زمانی که هماهنگی عصبی-عضلانی در بالاترین سطح قرار دارد.";
                    metrics = [
                        { label: "پنجره بهینه تمرین", value: "۱۷:۰۰ - ۱۹:۳۰", status: "optimal" },
                        { label: "میزان گلیکوژن", value: "۹۲٪ پر", status: "good" },
                    ];
                    tip = "یک ساعت قبل از شروع تمرین، یک میان‌وعده کربوهیدراتی سبک میل کنید.";
                } else if (text.includes("کالری") || text.includes("زون") || text.includes("چربی")) {
                    botText = "تا این لحظه ۶۵۰ کیلوکالری از هدف ۸۰۰ تایی خود را سوزانده‌اید. با ۴۰ دقیقه تمرین در زون ۲ و ۳ (ضربان ۱۲۵ تا ۱۴۸ bpm) می‌توانید به بیشترین نرخ اکسیداسیون چربی دست پیدا کنید.";
                    metrics = [
                        { label: "کالری مصرفی", value: "۶۵۰ kcal", status: "good" },
                        { label: "زون چربی‌سوزی", value: "۱۲۵ - ۱۴۲ bpm", status: "optimal" },
                    ];
                    tip = "نوشیدن آب در فواصل ۱۰ دقیقه‌ای در حین تمرین باعث جلوگیری از افت متابولیسم می‌شود.";
                } else {
                    botText = `برنامه تمرینی سفارشی برای: "${text}". سیستم هوش مصنوعی عضلات درگیر و استقامت قلبی شما را ارزیابی کرده و شدت تمرین را روی سطح متوسط-پیشرفته تنظیم کرد.`;
                    metrics = [
                        { label: "سطح بازدهی", value: "سطح ۴ از ۵", status: "good" },
                        { label: "مدت توصیه شده", value: "۴۵ دقیقه", status: "optimal" },
                    ];
                    tip = "حرکات کششی ایستا را در پایان تمرین به مدت ۵ دقیقه فراموش نکنید.";
                }
            }

            const botMsg: ChatMessage = {
                id: `bot-${Date.now()}`,
                sender: "bot",
                text: botText,
                time: "همین الان",
                metrics,
                tip,
            };

            setMessages((prev) => [...prev, botMsg]);
            setIsTyping(false);
        }, 750);
    };

    const handleReaction = (msgId: string, reactionType: "up" | "down") => {
        setMessages((prev) =>
            prev.map((msg) => (msg.id === msgId ? { ...msg, reaction: msg.reaction === reactionType ? null : reactionType } : msg))
        );
    };

    const handleCopy = (msgId: string, text: string) => {
        navigator.clipboard?.writeText(text);
        setCopiedId(msgId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleSpeak = (msgId: string) => {
        if (isSpeakingId === msgId) {
            setIsSpeakingId(null);
        } else {
            setIsSpeakingId(msgId);
            setTimeout(() => setIsSpeakingId(null), 4000);
        }
    };

    const handleVoiceInput = () => {
        if (isListening) return;
        setIsListening(true);
        setTimeout(() => {
            const prompt = isHealth ? "وضعیت ضربان قلب در استراحت چگونه است؟" : "پیشنهاد تمرین هوازی امروز چیه؟";
            setInputValue(prompt);
            setIsListening(false);
        }, 1200);
    };

    const handleReset = () => {
        setMessages(initialMessages);
        setInputValue("");
        setIsTyping(false);
    };

    return (
        <motion.div
            className="mt-5"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4 }}
        >
            <div
                className="relative rounded-3xl overflow-hidden border"
                style={{
                    background: "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(248,250,252,0.65) 100%)",
                    borderColor: isHealth ? "rgba(37,99,235,0.25)" : "rgba(22,163,74,0.25)",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    boxShadow: `0 12px 40px ${color}15, 0 2px 8px rgba(0,0,0,0.04)`,
                }}
            >
                {/* Neon Top Rail */}
                <div
                    className="absolute top-0 left-0 right-0 h-[2.5px]"
                    style={{
                        background: isHealth
                            ? "linear-gradient(90deg, transparent, #2563EB, #60A5FA, transparent)"
                            : "linear-gradient(90deg, transparent, #16A34A, #4ADE80, transparent)",
                    }}
                />

                {/* Header */}
                <div className="p-3.5 sm:p-5 pb-3 border-b border-gray-200/50 flex items-center justify-between gap-3 bg-white/40">
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                        {/* Avatar Icon: Hidden on mobile to avoid clutter */}
                        <div className="hidden sm:block relative flex-shrink-0">
                            <motion.div
                                className={`w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br ${gradFrom} ${gradTo} rounded-2xl flex items-center justify-center shadow-md`}
                                animate={{
                                    boxShadow: [`0 4px 14px ${color}35`, `0 6px 20px ${color}55`, `0 4px 14px ${color}35`],
                                }}
                                transition={{ duration: 2.5, repeat: Infinity }}
                            >
                                <Icon className="text-white text-base sm:text-lg" />
                            </motion.div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                            </span>
                        </div>

                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className={`text-sm sm:text-base font-extrabold truncate ${titleColor}`}>{assistantTitle}</span>
                                {/* Badge: Hidden on mobile */}
                                <span className="hidden sm:inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-white border border-gray-200/80 text-gray-500 shadow-2xs">
                                    AI v3.5
                                </span>
                            </div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                                <span className="truncate">{t("chat.onlineReady", { ns: "ai-feature" })}</span>
                                <span className="hidden sm:inline text-gray-300">·</span>
                                <span className="hidden sm:inline text-gray-400 font-normal">پردازش آنی بیومتریک</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        <motion.button
                            type="button"
                            onClick={handleReset}
                            title="شروع مجدد گفتگو"
                            aria-label="شروع مجدد گفتگو"
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-white/70 hover:bg-white text-gray-400 hover:text-gray-700 border border-gray-200/70 flex items-center justify-center transition-all shadow-xs"
                            whileHover={{ rotate: 180, scale: 1.05 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <FaRedo className="text-[10px] sm:text-[11px]" />
                        </motion.button>
                        <motion.span
                            className={`hidden md:flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border shadow-2xs ${pillBg}`}
                            animate={{ scale: [1, 1.02, 1] }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            <FaMagic className="text-[10px]" />
                            {isHealth ? "هوش فعال بالینی" : "مربی هوشمند فعال"}
                        </motion.span>
                    </div>
                </div>

                {/* Quick Prompts Bar */}
                <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-gray-50/70 via-white/50 to-gray-50/70 border-b border-gray-100 flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
                    <span className="hidden sm:flex text-[10px] font-bold text-gray-400 whitespace-nowrap items-center gap-1 pl-1">
                        <FaLightbulb className="text-amber-500 text-[11px]" />
                        پیشنهادها:
                    </span>
                    {quickPrompts.map((p) => {
                        const PIcon = p.icon;
                        return (
                            <motion.button
                                key={p.id}
                                type="button"
                                onClick={() => handleSend(p.label)}
                                className="px-2.5 py-1 rounded-xl text-[11px] font-medium whitespace-nowrap bg-white hover:bg-white text-gray-700 hover:text-gray-900 border border-gray-200/80 hover:border-blue-300 shadow-2xs flex items-center gap-1.5 transition-all flex-shrink-0"
                                whileHover={{ scale: 1.03, y: -1, borderColor: color }}
                                whileTap={{ scale: 0.96 }}
                            >
                                <PIcon className="text-[10px]" style={{ color }} />
                                {p.label}
                            </motion.button>
                        );
                    })}
                </div>

                {/* Messages Container */}
                <div ref={chatContainerRef} className="p-3 sm:p-5 space-y-3 sm:space-y-4 max-h-[360px] min-h-[200px] overflow-y-auto custom-scrollbar">
                    <AnimatePresence initial={false}>
                        {messages.map((msg) => {
                            const isBot = msg.sender === "bot";
                            return (
                                <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.25 }}
                                    className={`flex ${isBot ? "justify-start" : "justify-end"}`}
                                >
                                    <div className={`max-w-[96%] sm:max-w-[85%] flex gap-2 ${isBot ? "flex-row" : "flex-row-reverse"}`}>
                                        {/* Avatar: Hidden on mobile to maximize chat width and remove clutter */}
                                        {isBot && (
                                            <div className="hidden sm:flex flex-shrink-0 mt-0.5">
                                                <div
                                                    className={`w-7 h-7 rounded-xl bg-gradient-to-br ${gradFrom} ${gradTo} flex items-center justify-center text-white text-[11px] shadow-sm`}
                                                >
                                                    <FaRobot />
                                                </div>
                                            </div>
                                        )}

                                        {/* Bubble */}
                                        <div
                                            className={`rounded-2xl p-3 sm:p-3.5 shadow-sm text-sm leading-relaxed relative ${
                                                isBot
                                                    ? `bg-white/95 text-gray-800 border ${borderColor} rounded-tl-sm`
                                                    : `bg-gradient-to-r ${sendBtnFrom} text-white rounded-tr-sm shadow-md`
                                            }`}
                                        >
                                            {/* Text Content */}
                                            <p className={`text-xs sm:text-[13px] font-normal leading-relaxed ${isBot ? "text-gray-800" : "text-white"}`}>
                                                {msg.text}
                                            </p>

                                            {/* Structured Metrics Card (if available on bot message) */}
                                            {isBot && msg.metrics && msg.metrics.length > 0 && (
                                                <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 pt-2 border-t border-gray-100">
                                                    {msg.metrics.map((m, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="p-2 rounded-xl bg-gray-50/90 border border-gray-200/60 flex flex-col gap-0.5"
                                                        >
                                                            <span className="text-[10px] text-gray-500 font-medium">{m.label}</span>
                                                            <span
                                                                className="text-xs font-bold flex items-center gap-1"
                                                                style={{ color: m.status === "optimal" ? (isHealth ? "#155DFC" : "#16A34A") : "#374151" }}
                                                            >
                                                                <FaCheckCircle className="text-[10px] text-emerald-500" />
                                                                {m.value}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Actionable Tip */}
                                            {isBot && msg.tip && (
                                                <div
                                                    className="mt-2.5 p-2 rounded-xl text-[11px] flex items-start gap-2 border"
                                                    style={{
                                                        backgroundColor: isHealth ? "rgba(239,246,255,0.7)" : "rgba(240,253,244,0.7)",
                                                        borderColor: isHealth ? "rgba(191,219,254,0.6)" : "rgba(187,247,208,0.6)",
                                                        color: isHealth ? "#1E40AF" : "#166534",
                                                    }}
                                                >
                                                    <FaLightbulb className="text-amber-500 text-xs flex-shrink-0 mt-0.5" />
                                                    <span className="font-medium">{msg.tip}</span>
                                                </div>
                                            )}

                                            {/* Footer Actions & Metadata */}
                                            <div className="flex items-center justify-between gap-3 mt-2.5 pt-1.5 border-t border-gray-200/40 text-[10px]">
                                                <span className={isBot ? "text-gray-400" : "text-blue-100"}>{msg.time}</span>

                                                {isBot && (
                                                    <div className="flex items-center gap-1 sm:gap-1.5">
                                                        {/* Speak Voice simulation */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSpeak(msg.id)}
                                                            className={`p-1 rounded-md transition-colors ${
                                                                isSpeakingId === msg.id ? "bg-blue-100 text-blue-600 font-bold" : "text-gray-400 hover:text-gray-700"
                                                            }`}
                                                            title="شنیدن پاسخ صوتی"
                                                            aria-label="شنیدن پاسخ صوتی"
                                                        >
                                                            {isSpeakingId === msg.id ? (
                                                                <span className="flex items-center gap-1 text-[10px]">
                                                                    <span className="w-1 h-2 bg-blue-600 rounded-full animate-bounce" />
                                                                    <span className="w-1 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                                                                    <span className="w-1 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                                                                </span>
                                                            ) : (
                                                                <FaVolumeUp className="text-[11px]" />
                                                            )}
                                                        </button>

                                                        {/* Copy */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopy(msg.id, msg.text)}
                                                            className="p-1 rounded-md text-gray-400 hover:text-gray-700 transition-colors"
                                                            title="کپی متن"
                                                            aria-label="کپی متن پیام"
                                                        >
                                                            {copiedId === msg.id ? <FaCheck className="text-emerald-500 text-[10px]" /> : <FaCopy className="text-[10px]" />}
                                                        </button>

                                                        {/* Feedback Thumbs */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReaction(msg.id, "up")}
                                                            className={`p-1 rounded-md transition-all ${
                                                                msg.reaction === "up" ? "text-emerald-600 bg-emerald-50" : "text-gray-400 hover:text-emerald-600"
                                                            }`}
                                                            title="مفید بود"
                                                            aria-label="پاسخ مفید بود"
                                                        >
                                                            <FaThumbsUp className="text-[10px]" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReaction(msg.id, "down")}
                                                            className={`p-1 rounded-md transition-all ${
                                                                msg.reaction === "down" ? "text-rose-600 bg-rose-50" : "text-gray-400 hover:text-rose-600"
                                                            }`}
                                                            title="نیاز به اصلاح"
                                                            aria-label="پاسخ نیاز به اصلاح دارد"
                                                        >
                                                            <FaThumbsDown className="text-[10px]" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>

                    {/* Typing Animation */}
                    {isTyping && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2"
                        >
                            <div className="hidden sm:flex w-7 h-7 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 items-center justify-center text-white text-[11px] shadow-sm">
                                <FaRobot />
                            </div>
                            <div
                                className="px-3 py-2 rounded-2xl bg-white border border-gray-200/80 shadow-xs flex items-center gap-2"
                            >
                                <TypingDots color={color} />
                                <span className="text-[10px] sm:text-[11px] text-gray-500 font-medium">
                                    دستیار در حال تحلیل شاخص‌های زیستی...
                                </span>
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* Input Bar */}
                <div className="p-2.5 sm:p-4 bg-white/60 border-t border-gray-200/50 backdrop-blur-md">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSend();
                        }}
                        className="flex items-center gap-1.5 sm:gap-2 bg-white rounded-2xl p-1 sm:p-1.5 pl-1.5 sm:pl-2 border border-gray-200/90 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all"
                    >
                        {/* Voice Input Button */}
                        <motion.button
                            type="button"
                            onClick={handleVoiceInput}
                            title="ورودی صوتی"
                            aria-label={isListening ? "توقف ضبط صدا" : "ورودی صوتی"}
                            className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center transition-all ${
                                isListening
                                    ? "bg-rose-500 text-white animate-pulse shadow-md"
                                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                            }`}
                            whileTap={{ scale: 0.92 }}
                        >
                            <FaMicrophone className={`text-xs ${isListening ? "animate-bounce" : ""}`} />
                        </motion.button>

                        {/* Text Input Field */}
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={isListening ? "در حال گوش دادن..." : isHealth ? "سوال سلامت یا بیومتریک خود را بنویسید..." : "سوال درباره تمرین، کالری یا ریکاوری..."}
                            className="flex-1 bg-transparent px-1.5 sm:px-2 text-xs sm:text-sm text-gray-800 placeholder-gray-400 outline-none"
                            dir="auto"
                        />

                        {/* Send Button */}
                        <motion.button
                            type="submit"
                            disabled={!inputValue.trim() || isTyping}
                            aria-label="ارسال پیام"
                            className={`h-8 sm:h-9 px-3 sm:px-3.5 rounded-xl flex items-center justify-center gap-1.5 text-white font-medium text-xs shadow-md transition-all ${
                                !inputValue.trim() || isTyping
                                    ? "bg-gray-300 cursor-not-allowed opacity-60"
                                    : `bg-gradient-to-r ${sendBtnFrom} hover:opacity-95 hover:shadow-lg`
                            }`}
                            whileHover={inputValue.trim() && !isTyping ? { scale: 1.04 } : {}}
                            whileTap={inputValue.trim() && !isTyping ? { scale: 0.95 } : {}}
                        >
                            <span className="hidden sm:inline">ارسال</span>
                            <FaPaperPlane className="text-[10px]" />
                        </motion.button>
                    </form>
                </div>
            </div>
        </motion.div>
    );
};

// ─── Large Feature Card ───────────────────────────────────────────────────────
interface LargeCardProps {
    feature: { icon: React.ReactNode; title: string; subtitle: string; color: string };
    assistantType: "health" | "sport";
    widgetSlot: React.ReactNode;
    className?: string;
}

const LargeCard: React.FC<LargeCardProps> = ({ feature, assistantType, widgetSlot, className = "" }) => {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div
            ref={cardRef}
            className={`group relative rounded-2xl sm:rounded-[24px] p-4 sm:p-6 md:p-7 overflow-hidden cursor-pointer bg-white/80 backdrop-blur-xl transition-all duration-300 ${className}`}
            style={{
                border: isHovered ? `1px solid ${feature.color}45` : "1px solid rgba(226, 232, 240, 0.85)",
                boxShadow: isHovered
                    ? `0 20px 45px -12px ${feature.color}20, 0 4px 16px rgba(0,0,0,0.03)`
                    : "0 4px 20px -4px rgba(15, 23, 42, 0.05), 0 2px 6px -2px rgba(15, 23, 42, 0.03)",
            }}
            whileHover={{ y: -2 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Dynamic cursor-following spotlight */}
            <div
                className="pointer-events-none absolute -inset-px transition-opacity duration-300 rounded-2xl sm:rounded-[24px]"
                style={{
                    opacity: isHovered ? 1 : 0,
                    background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, ${feature.color}12, transparent 70%)`,
                }}
            />

            {/* Subtle top accent line on hover */}
            <div
                className="absolute top-0 inset-x-0 h-[2px] transition-all duration-500 origin-center scale-x-0 group-hover:scale-x-100"
                style={{
                    background: `linear-gradient(90deg, transparent, ${feature.color}, transparent)`,
                }}
            />

            <h3
                className="text-lg sm:text-xl font-bold mb-1.5 sm:mb-2 transition-colors duration-300 text-slate-900 group-hover:text-slate-950"
                style={{ color: isHovered ? feature.color : undefined }}
            >
                {feature.title}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed transition-colors duration-300 group-hover:text-slate-700">
                {feature.subtitle}
            </p>

            <AnimatePresence mode="wait">
                <div key={assistantType} className="flex-1 flex flex-col">{widgetSlot}</div>
            </AnimatePresence>
        </motion.div>
    );
};

// ─── Switcher Panel ───────────────────────────────────────────────────────────
const SwitcherPanel: React.FC<{
    assistantType: "health" | "sport";
    setAssistantType: (t: "health" | "sport") => void;
    t: any;
    mobile?: boolean;
}> = ({ assistantType, setAssistantType, t, mobile }) => (
    <motion.div
        className={`${
            mobile
                ? "flex md:hidden justify-center w-full max-w-sm mx-auto p-1.5 rounded-2xl bg-slate-100/90 border border-slate-200/80 shadow-sm gap-1.5"
                : "hidden md:flex items-center gap-2.5 rounded-[26px] p-2 bg-white/80 border border-slate-200/80 backdrop-blur-xl shadow-lg shadow-slate-900/5"
        }`}
        style={{
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
        }}
        initial={{ opacity: 0, x: mobile ? 0 : 40, y: mobile ? 16 : 0 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
    >
        <SwitcherBtn
            active={assistantType === "health"}
            color="#155DFC"
            label={t("switcher.health.label", { ns: "ai-feature" })}
            icon={(active) => <HealthMultiLayerIcon active={active} />}
            onClick={() => setAssistantType("health")}
            isMobile={mobile}
        />
        <SwitcherBtn
            active={assistantType === "sport"}
            color="#16A34A"
            label={t("switcher.sport.label", { ns: "ai-feature" })}
            icon={(active) => <SportMultiLayerIcon active={active} />}
            onClick={() => setAssistantType("sport")}
            isMobile={mobile}
        />
    </motion.div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AIFeature: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "ai-feature";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) i18n.addResourceBundle(lng, ns, data, true, true);
        });
    }, [i18n]);

    const controls = useAnimation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(sectionRef, { once: false, amount: 0.25 });
    const [assistantType, setAssistantTypeState] = useState<"health" | "sport">("health");
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const setAssistantType = (type: "health" | "sport") => {
        setHasUserInteracted(true);
        setAssistantTypeState(type);
    };

    useEffect(() => {
        if (isInView) controls.start("visible");
    }, [controls, isInView]);

    // Timer starts ONLY when in view AND user has not interacted with any part of the component
    useEffect(() => {
        if (!isInView || hasUserInteracted) return;

        const id = setInterval(() => {
            setAssistantTypeState((p) => (p === "health" ? "sport" : "health"));
        }, 16000);

        return () => clearInterval(id);
    }, [isInView, hasUserInteracted]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
    };

    const mk = (ns: "health" | "sport", i: number) => ({
        icon: <span className="text-2xl"><FaRunning /></span>,
        title: t(`features.${ns}.${i}.title`, { ns: "ai-feature" }),
        subtitle: t(`features.${ns}.${i}.subtitle`, { ns: "ai-feature" }),
        category: t(`features.${ns}.${i}.category`, { ns: "ai-feature" }),
        color: ns === "health" ? "#155DFC" : "#16A34A",
        hoverIcon: <span className="text-xs"><FaRunning /></span>,
        hoverText: t(`features.${ns}.${i}.hoverText`, { ns: "ai-feature" }),
    });

    const healthFeatures = [
        { ...mk("health", 0), icon: <span className="text-2xl"><FaHeartbeat /></span>, hoverIcon: <span className="text-xs"><FaHeartbeat /></span> },
        { ...mk("health", 1), icon: <span className="text-2xl"><FaBell /></span>, hoverIcon: <span className="text-xs"><FaExclamationTriangle /></span> },
        { ...mk("health", 2), icon: <span className="text-2xl"><FaHeart /></span>, hoverIcon: <span className="text-xs"><FaBrain /></span> },
        { ...mk("health", 3), icon: <span className="text-2xl"><FaChartLine /></span>, hoverIcon: <span className="text-xs"><FaChartLine /></span> },
        { ...mk("health", 4), icon: <span className="text-2xl"><FaFlask /></span>, hoverIcon: <span className="text-xs"><FaMicroscope /></span> },
        { ...mk("health", 5), icon: <span className="text-2xl"><FaStethoscope /></span>, hoverIcon: <span className="text-xs"><FaStethoscope /></span> },
    ];
    const sportFeatures = [
        { ...mk("sport", 0), icon: <span className="text-2xl"><FaBolt /></span>, hoverIcon: <span className="text-xs"><FaBolt /></span> },
        { ...mk("sport", 1), icon: <span className="text-2xl"><FaHeartbeat /></span>, hoverIcon: <span className="text-xs"><FaHeartbeat /></span> },
        { ...mk("sport", 2), icon: <span className="text-2xl"><FaFire /></span>, hoverIcon: <span className="text-xs"><FaFire /></span> },
        { ...mk("sport", 3), icon: <span className="text-2xl"><FaStopwatch /></span>, hoverIcon: <span className="text-xs"><FaStopwatch /></span> },
        { ...mk("sport", 4), icon: <span className="text-2xl"><FaTrophy /></span>, hoverIcon: <span className="text-xs"><FaTrophy /></span> },
        { ...mk("sport", 5), icon: <span className="text-2xl"><FaDumbbell /></span>, hoverIcon: <span className="text-xs"><FaDumbbell /></span> },
    ];

    const features = assistantType === "health" ? healthFeatures : sportFeatures;
    const themeColor = assistantType === "health" ? "#155DFC" : "#16A34A";

    return (
        <section
            ref={sectionRef}
            onClickCapture={() => setHasUserInteracted(true)}
            onTouchStartCapture={() => setHasUserInteracted(true)}
            className="py-16 md:py-24 relative overflow-hidden"
            id="ai-features"
        >
            {/* Ambient blobs */}
            <motion.div
                className="absolute pointer-events-none"
                style={{
                    width: 500,
                    height: 500,
                    borderRadius: "50%",
                    background: `radial-gradient(ellipse, ${themeColor}12 0%, transparent 70%)`,
                    filter: "blur(60px)",
                    top: "5%",
                    right: "10%",
                    transition: "background 1s ease",
                }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute pointer-events-none"
                style={{
                    width: 350,
                    height: 350,
                    borderRadius: "50%",
                    background: "radial-gradient(ellipse, rgba(21,93,252,0.06) 0%, transparent 70%)",
                    filter: "blur(50px)",
                    bottom: "10%",
                    left: "5%",
                }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            />

            <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div className="mb-14" initial={{ opacity: 0, y: -16 }} animate={controls} variants={{ visible: { opacity: 1, y: 0 } }} transition={{ duration: 0.6 }}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 mb-10">
                        {/* Text */}
                        <div className="flex md:w-3/5 flex-col gap-4 px-2 md:px-6 py-2 relative z-10">
                            <motion.div
                                initial={{ opacity: 0, x: -16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="hidden md:block text-base font-bold tracking-wide leading-normal pb-1"
                                style={{
                                    background: "linear-gradient(135deg, #155DFC 0%, #2563EB 50%, #38BDF8 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                    paddingTop: "4px",
                                    paddingBottom: "8px",
                                }}
                            >
                                {t("header.tag", { ns: "ai-feature" })}
                            </motion.div>

                            <motion.div className="flex flex-col gap-1.5" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 leading-normal pb-1">{t("header.h1Prefix", { ns: "ai-feature" })}</h2>
                                    <motion.h2
                                        className="text-3xl md:text-4xl font-bold leading-normal"
                                        style={{
                                            background: "linear-gradient(135deg, #155DFC 0%, #2563EB 50%, #38BDF8 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                            paddingTop: "4px",
                                            paddingBottom: "10px",
                                        }}
                                        whileHover={{ scale: 1.03 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        {t("header.h1Highlight", { ns: "ai-feature" })}
                                    </motion.h2>
                                </div>
                                <h3 className="text-3xl md:text-4xl font-normal text-neutral-900 leading-normal">{t("header.h2", { ns: "ai-feature" })}</h3>
                            </motion.div>

                            <motion.p className="text-base text-neutral-600 leading-[200%]" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
                                {t("header.description", { ns: "ai-feature" })}
                            </motion.p>
                        </div>

                        {/* Desktop switcher */}
                        <SwitcherPanel assistantType={assistantType} setAssistantType={setAssistantType} t={t} />
                    </div>

                    {/* Mobile switcher */}
                    <SwitcherPanel assistantType={assistantType} setAssistantType={setAssistantType} t={t} mobile />
                </motion.div>

                {/* Responsive Bento Grid Layout */}
                <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 w-full" variants={containerVariants} initial="hidden" animate={controls}>
                    {/* Left Bento Column (lg: 5 cols) - Compact Biometric Analysis & 2 Feature Cards */}
                    <div className="lg:col-span-5 flex flex-col gap-5 sm:gap-6">
                        {/* 1. Compact Biometric Dashboard / Readiness Widget */}
                        <LargeCard
                            feature={features[0]}
                            assistantType={assistantType}
                            widgetSlot={<AnimatePresence mode="wait">{assistantType === "health" ? <HealthWidget key="hw" t={t} /> : <SportWidget key="sw" t={t} />}</AnimatePresence>}
                        />

                        {/* 2 Small Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                            <SmallCard key={`${assistantType}-sm-bento-0`} feature={features[1]} delay={0.06} />
                            <SmallCard key={`${assistantType}-sm-bento-1`} feature={features[2]} delay={0.12} />
                        </div>
                    </div>

                    {/* Right Bento Column (lg: 7 cols) - Unified Interactive AI Chatbot */}
                    <div className="lg:col-span-7 flex flex-col">
                        {/* 2. Interactive AI Chat Assistant (Full Height & Seamless) */}
                        <LargeCard
                            feature={features[5]}
                            assistantType={assistantType}
                            className="h-full flex flex-col justify-between"
                            widgetSlot={
                                <AnimatePresence mode="wait">
                                    <ChatWidget key={`chat-${assistantType}`} type={assistantType} featureTitle={features[5].title} t={t} />
                                </AnimatePresence>
                            }
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default AIFeature;

